import { atom } from 'jotai';
import { atomWithDefault, loadable } from 'jotai/utils';
import { withAtomEffect } from 'jotai-effect';
import { atomFamily } from 'jotai-family';
import * as Sentry from '@sentry/react';
import { dequal } from 'dequal/lite';

import { atomFamilyWithDefault, createSimpleState, createState } from '@rewardopl/jotai';
import { get as networkGet } from '@rewardopl/utils/network';
import { stringify } from '@rewardopl/utils/url';

import { APP_ID, VENDOR_BUSINESS_ID, VENDOR_CARD_ID } from './constants';

import { PROD } from './env';

import type {
  Business,
  Card,
  CardSubscription,
  Challenge,
  Config,
  CouponActivation,
  CouponWithDynamicProperties,
  HeavilyFilteredUser,
  Ingredient,
  IntegrationOrder,
  Message,
  NotRedeemedRewardTransaction,
  Offer,
  PlaceWithDynamicPropertiesForUser,
  PointsTransaction,
  PollWithDynamicProperties,
  ProductWithDynamicPropertiesForUser,
  PromotionGroup,
  RewardTransaction,
  RewardWithDynamicProperties,
  Transaction,
  User,
} from '@rewardopl/types';
import type { SetStateAction } from 'jotai';

type ChallengeRanking = { user: HeavilyFilteredUser; score: number }[];

export const configState = atom(
  () =>
    networkGet('/api/config', {
      headers: {
        'app-id': APP_ID,
      },
    }) as Promise<Config>,
);

export const configGoorderUrlState = atom(async (get) => {
  const config = await get(configState);

  return config.goorder_url;
});

export const configLoginState = atom(async (get) => {
  const config = await get(configState);

  if (!config.login) {
    throw new Error('Login config not found');
  }

  return config.login;
});

export const configLoginLoadable = loadable(configLoginState);

function syncUser(user: User | null) {
  if (PROD) {
    Sentry.setUser(user ? { id: user._id, email: user.email } : null);
  }

  localStorage.setItem('user', JSON.stringify(user));
}

async function getUser(signal?: AbortSignal): Promise<User | null> {
  let response = null;
  try {
    response = await (networkGet('/api/users/current', { signal }) as Promise<User>);
  } catch {}

  syncUser(response);

  return response;
}

const userWasCached = Boolean(localStorage.getItem('user'));

function readCachedUser(): User | null | undefined {
  const rawLocalStorageValue = localStorage.getItem('user');

  if (rawLocalStorageValue) {
    try {
      return JSON.parse(rawLocalStorageValue) as User | null;
    } catch {}
  }

  return undefined;
}

const defaultMaybeCurrentUserBaseAtom = atomWithDefault<User | null | Promise<User | null>>(() => {
  const cached = readCachedUser();

  if (cached !== undefined) {
    return cached;
  }

  return getUser();
});

const defaultMaybeCurrentUserState = withAtomEffect(
  defaultMaybeCurrentUserBaseAtom,
  (_get, set) => {
    // Only refetch if user was initially in localStorage, therefore potentially stale
    if (!userWasCached) {
      return;
    }

    const cached = readCachedUser();

    const controller = new AbortController();

    const { signal } = controller;

    getUser(signal).then((nextValue) => {
      if (signal.aborted) {
        return;
      }

      if (!dequal(nextValue, cached)) {
        set(defaultMaybeCurrentUserBaseAtom, nextValue);
      }
    });

    return () => {
      controller.abort();
    };
  },
);

export const maybeCurrentUserState = atom<
  User | null | Promise<User | null>,
  [SetStateAction<User | null>],
  void
>(
  (get) => get(defaultMaybeCurrentUserState),
  async (get, set, nextValueOrSetter: SetStateAction<User | null>) => {
    const nextValue =
      typeof nextValueOrSetter === 'function'
        ? nextValueOrSetter(await get(defaultMaybeCurrentUserState))
        : nextValueOrSetter;

    localStorage.setItem('user', JSON.stringify(nextValue));

    set(defaultMaybeCurrentUserState, nextValue);
  },
);

export const maybeCurrentUserLoadable = loadable(maybeCurrentUserState);

export const currentUserState = atom(
  async (get) => {
    const maybeUser = await get(maybeCurrentUserState);

    if (!maybeUser) {
      throw new Error('User not found');
    }

    return maybeUser;
  },
  (_get, set, nextValue: User) => {
    set(maybeCurrentUserState, nextValue);
  },
);

export const currentUserIdState = atom(async (get) => (await get(currentUserState))._id);

const maybeBusinessQuery = atomFamily((id: string | undefined) =>
  atom(() => (id ? (networkGet(`/api/businesses/${id}`) as Promise<Business>) : null)),
);

export const businessQuery = atomFamily((id: string) =>
  atom(async (get) => {
    const business = await get(maybeBusinessQuery(id));

    if (!business) {
      throw new Error('Business not found');
    }

    return business;
  }),
);

export const { itemsAtom: cardSubscriptionsState, itemAtomFamily: cardSubscriptionQuery } =
  createSimpleState<CardSubscription, CardSubscription['_id']>(
    () => networkGet('/api/users/current/card_subscriptions') as Promise<CardSubscription[]>,
    (id) => () =>
      networkGet(`/api/users/current/card_subscriptions/${id}`) as Promise<CardSubscription>,
  );

export const { itemsAtomFamily: integrationOrdersQuery, itemAtomFamily: integrationOrderQuery } =
  createState(
    (param: string | { id: string; offset: number; limit: number }) => () => {
      const { id, ...rest } = typeof param === 'string' ? { id: param } : param;

      return networkGet(
        `/api/card_subscriptions/${id}/integration_orders${stringify(rest)}`,
      ) as Promise<IntegrationOrder[]>;
    },
    ([baseId, id]) =>
      () =>
        networkGet(
          `/api/card_subscriptions/${baseId}/integration_orders/${id}`,
        ) as Promise<IntegrationOrder>,
  );

const { itemsAtom: messagesState, itemAtomFamily: messageQuery } = createSimpleState<
  Message,
  Message['_id']
>(
  () => networkGet('/api/users/current/messages') as Promise<Message[]>,
  (id) => () => networkGet(`/api/users/current/messages/${id}`) as Promise<Message>,
);

export const messagePollQuery = atomFamilyWithDefault(
  (id) => () =>
    networkGet(
      `/api/users/current/messages/${id}/poll`,
    ) as Promise<PollWithDynamicProperties | null>,
);

export const { itemsAtomFamily: placeProductsQuery, itemAtomFamily: placeProductQuery } =
  createState(
    (id) => () =>
      networkGet(`/api/places/${id}/products`) as Promise<ProductWithDynamicPropertiesForUser[]>,
    ([baseId, id]) =>
      () =>
        networkGet(
          `/api/places/${baseId}/products/${id}`,
        ) as Promise<ProductWithDynamicPropertiesForUser>,
  );

export const { itemsAtomFamily: transactionsQuery, itemAtomFamily: transactionQuery } = createState(
  (param: string | { id: string; offset: number; limit: number }) => () => {
    const { id, ...rest } = typeof param === 'string' ? { id: param } : param;

    return networkGet(`/api/card_subscriptions/${id}/transactions${stringify(rest)}`) as Promise<
      Transaction[]
    >;
  },
  ([baseId, id]) =>
    () =>
      networkGet(`/api/card_subscriptions/${baseId}/transactions/${id}`) as Promise<Transaction>,
);

export const vendorBusinessState = atom(async (get) => get(businessQuery(VENDOR_BUSINESS_ID)));

export const vendorCardState = atom(
  () => networkGet(`/api/cards/${VENDOR_CARD_ID}`) as Promise<Card>,
);

export const vendorCardLoadable = loadable(vendorCardState);

export const { itemsAtom: vendorCardChallengesState, itemAtomFamily: vendorCardChallengeQuery } =
  createSimpleState<Challenge, Challenge['_id']>(
    () => networkGet(`/api/cards/${VENDOR_CARD_ID}/challenges`) as Promise<Challenge[]>,
    (id) => () => networkGet(`/api/cards/${VENDOR_CARD_ID}/challenges/${id}`) as Promise<Challenge>,
  );

export const vendorCardChallengeRankingQuery = atomFamilyWithDefault(
  (id) => () =>
    networkGet(
      `/api/cards/${VENDOR_CARD_ID}/challenges/${id}/ranking`,
    ) as Promise<ChallengeRanking>,
);

export const { itemsAtom: vendorCardCouponsState, itemAtomFamily: vendorCardCouponQuery } =
  createSimpleState<CouponWithDynamicProperties, CouponWithDynamicProperties['_id']>(
    () =>
      networkGet(`/api/cards/${VENDOR_CARD_ID}/coupons`) as Promise<CouponWithDynamicProperties[]>,
    (id) => () =>
      networkGet(
        `/api/cards/${VENDOR_CARD_ID}/coupons/${id}`,
      ) as Promise<CouponWithDynamicProperties>,
  );

export const {
  itemsAtomFamily: vendorCardCouponActivationsQuery,
  itemAtomFamily: vendorCardCouponActivationQuery,
} = createState(
  (id) => () =>
    networkGet(`/api/cards/${VENDOR_CARD_ID}/coupons/${id}/coupon_activations`) as Promise<
      CouponActivation[]
    >,
  ([baseId, id]) =>
    () =>
      networkGet(
        `/api/cards/${VENDOR_CARD_ID}/coupons/${baseId}/coupon_activations/${id}`,
      ) as Promise<CouponActivation>,
);

export const { itemsAtom: vendorCardOffersState, itemAtomFamily: vendorCardOfferQuery } =
  createSimpleState<Offer, Offer['_id']>(
    () => networkGet(`/api/cards/${VENDOR_CARD_ID}/offers`) as Promise<Offer[]>,
    (id) => () => networkGet(`/api/cards/${VENDOR_CARD_ID}/offers/${id}`) as Promise<Offer>,
  );

export const vendorCardPromotionGroupsState = atom(
  async () =>
    networkGet(`/api/cards/${VENDOR_CARD_ID}/promotion_groups`) as Promise<PromotionGroup[]>,
);

export const { itemsAtom: vendorCardRewardsState, itemAtomFamily: vendorCardRewardQuery } =
  createSimpleState<RewardWithDynamicProperties, RewardWithDynamicProperties['_id']>(
    () =>
      networkGet(`/api/cards/${VENDOR_CARD_ID}/rewards`) as Promise<RewardWithDynamicProperties[]>,
    (id) => () =>
      networkGet(
        `/api/cards/${VENDOR_CARD_ID}/rewards/${id}`,
      ) as Promise<RewardWithDynamicProperties>,
  );

export const vendorCardSubscriptionState = atom(
  async (get) => {
    const cardSubscriptions = await get(cardSubscriptionsState);

    const vendorCardSubscription = cardSubscriptions.find(
      (cardSubscription) => cardSubscription.active && cardSubscription.card_id === VENDOR_CARD_ID,
    );

    if (!vendorCardSubscription) {
      throw new Error('Card subscription not found');
    }

    return vendorCardSubscription;
  },
  (_get, set, nextValueOrSetter: SetStateAction<CardSubscription>) => {
    set(cardSubscriptionsState, (prevCardSubscriptions) =>
      prevCardSubscriptions.map((cardSubscription) =>
        cardSubscription.card_id === VENDOR_CARD_ID
          ? typeof nextValueOrSetter === 'function'
            ? nextValueOrSetter(cardSubscription)
            : nextValueOrSetter
          : cardSubscription,
      ),
    );
  },
);

export const vendorCardSubscriptionBalanceState = atom(async (get) => {
  const vendorCardSubscription = await get(vendorCardSubscriptionState);

  return vendorCardSubscription.balance;
});

export const vendorCardSubscriptionIdState = atom(async (get) => {
  const vendorCardSubscription = await get(vendorCardSubscriptionState);

  return vendorCardSubscription._id;
});

export const { itemsAtom: vendorIngredientsState, itemAtomFamily: vendorIngredientQuery } =
  createSimpleState<Ingredient, Ingredient['_id']>(
    () => networkGet(`/api/businesses/${VENDOR_BUSINESS_ID}/ingredients`) as Promise<Ingredient[]>,
    (id) => () =>
      networkGet(`/api/businesses/${VENDOR_BUSINESS_ID}/ingredients/${id}`) as Promise<Ingredient>,
  );

function isVendorMessage(message: Message): message is Message & { business_id: Business['_id'] } {
  return message.business_id === VENDOR_BUSINESS_ID;
}

export const vendorMessagesState = atom(
  async (get) => {
    const messages = await get(messagesState);

    return messages.filter(isVendorMessage);
  },
  (_get, set, nextValueOrSetter: SetStateAction<Message[]>) => {
    set(messagesState, nextValueOrSetter);
  },
);

export const vendorMessageQuery = atomFamily((id: Message['_id']) =>
  atom(
    async (get) => {
      const message = await get(messageQuery(id));

      if (!isVendorMessage(message)) {
        throw new Error('Message not found');
      }

      return message;
    },
    (_get, set, nextValueOrSetter: SetStateAction<Message>) => {
      set(messageQuery(id), nextValueOrSetter);
    },
  ),
);

export const vendorMessagePollQuery = messagePollQuery;

const defaultVendorPlacesQuery = atomFamily(
  (
    params:
      | {
          categories?: string;
          includePartners?: boolean;
          includePlacesWithoutLocation?: boolean;
          lat?: number;
          limit?: number;
          lng?: number;
          search?: string;
        }
      | undefined = {},
  ) =>
    atomWithDefault<
      PlaceWithDynamicPropertiesForUser[] | Promise<PlaceWithDynamicPropertiesForUser[]>
    >(
      () =>
        networkGet(`/api/businesses/${VENDOR_BUSINESS_ID}/places${stringify(params)}`) as Promise<
          PlaceWithDynamicPropertiesForUser[]
        >,
    ),
  dequal,
);

export const vendorPlacesQuery = atomFamily(
  (
    params:
      | {
          categories?: string;
          includePartners?: boolean;
          includePlacesWithoutLocation?: boolean;
          lat?: number;
          limit?: number;
          lng?: number;
          search?: string;
        }
      | undefined,
  ) =>
    atom(
      async (get) => get(defaultVendorPlacesQuery(params)),
      (_get, set, nextValueOrSetter: SetStateAction<PlaceWithDynamicPropertiesForUser[]>) => {
        set(
          defaultVendorPlacesQuery(params),
          typeof nextValueOrSetter === 'function'
            ? async (prevValueOrPromise) => {
                const prevValue = await prevValueOrPromise;

                return nextValueOrSetter(prevValue);
              }
            : nextValueOrSetter,
        );
      },
    ),
  dequal,
);

export const { itemsAtom: vendorPlacesState, itemAtomFamily: vendorPlaceQuery } = createSimpleState<
  PlaceWithDynamicPropertiesForUser,
  PlaceWithDynamicPropertiesForUser['_id']
>(
  () =>
    networkGet(`/api/businesses/${VENDOR_BUSINESS_ID}/places`) as Promise<
      PlaceWithDynamicPropertiesForUser[]
    >,
  (id) => () =>
    networkGet(
      `/api/businesses/${VENDOR_BUSINESS_ID}/places/${id}`,
    ) as Promise<PlaceWithDynamicPropertiesForUser>,
);

export const { itemsAtom: vendorProductsState, itemAtomFamily: vendorProductQuery } =
  createSimpleState<
    ProductWithDynamicPropertiesForUser,
    ProductWithDynamicPropertiesForUser['_id']
  >(
    () =>
      networkGet(`/api/businesses/${VENDOR_BUSINESS_ID}/products`) as Promise<
        ProductWithDynamicPropertiesForUser[]
      >,
    (id) => () =>
      networkGet(
        `/api/businesses/${VENDOR_BUSINESS_ID}/products/${id}`,
      ) as Promise<ProductWithDynamicPropertiesForUser>,
  );

function getIsPointsTransaction(transaction: Transaction): transaction is PointsTransaction {
  return transaction.type === 'points';
}

export const pointsTransactionsQuery = atomFamily(
  (param: string | { id: string; offset: number; limit: number }) =>
    atom(async (get) => {
      const transactions = await get(transactionsQuery(param));

      return transactions.filter(getIsPointsTransaction);
    }),
  dequal,
);

function getIsRewardTransaction(transaction: Transaction): transaction is RewardTransaction {
  return transaction.type === 'reward';
}

function getIsRedeemableTransaction(
  transaction: Transaction,
): transaction is NotRedeemedRewardTransaction {
  return getIsRewardTransaction(transaction) && !transaction.redeemed && !transaction.returned;
}

export const redeemableTransactionsQuery = atomFamily((id: string) =>
  atom(
    async (get) => {
      const transactions = await get(transactionsQuery(id));

      return transactions.filter(getIsRedeemableTransaction);
    },
    (_get, set, nextValueOrGetter: SetStateAction<NotRedeemedRewardTransaction[]>) => {
      set(transactionsQuery(id), (prevValue) => {
        const nextValue =
          typeof nextValueOrGetter === 'function'
            ? nextValueOrGetter(prevValue.filter(getIsRedeemableTransaction))
            : nextValueOrGetter;

        return prevValue.filter((transaction) =>
          getIsRedeemableTransaction(transaction)
            ? nextValue.find((nextTransaction) => transaction._id === nextTransaction._id)
            : transaction,
        );
      });
    },
  ),
);

export const pendingCouponRefreshesState = atom<Card['_id'][]>([]);

export const pendingRewardRefreshesState = atom<Card['_id'][]>([]);

export const vendorUrgentMessagesState = atom(
  async (get) => {
    const messages = await get(vendorMessagesState);

    return messages.filter((message) => message.is_urgent);
  },
  (_get, set, nextValueOrSetter: SetStateAction<Message[]>) => {
    set(messagesState, nextValueOrSetter);
  },
);

export const lastGoorderUrlState = atom<string | null>(null);
